import React, { useState, useMemo } from "react";
import { motion } from "framer-motion";
import { useJourney } from "@/context/JourneyContext";
import { StageFrame, Eyebrow } from "@/components/StageFrame";
import { ConsultantNav } from "@/components/Navigation";
import { money } from "@/lib/api";
import { Home, Building2, ArrowRight, ArrowUp, ArrowDown, CheckCircle2 } from "lucide-react";

const RATE = 0.065;          // home loan interest rate
const HOME_G = 0.065;        // home capital growth p.a. (for home equity display)
const HORIZON = 30;
const START_YEAR = 2026;
const INV_PRICE = 650000;    // each investment property
const INV_LOAN = INV_PRICE * 0.8;   // 80% interest-only loan = 520,000

function monthlyRepay(principal, years) {
  const r = RATE / 12, n = years * 12;
  return principal * (r * (1 + r) ** n) / ((1 + r) ** n - 1);
}

const PORTFOLIOS = [
  { n: 1, label: "Balanced" },
  { n: 2, label: "Growth" },
  { n: 3, label: "Aggressive" },
];

function Ring({ years, year }) {
  const pct = Math.max(0.06, Math.min(1, 1 - (years - 2) / (HORIZON - 2)));
  const R = 92, C = 2 * Math.PI * R;
  return (
    <div className="relative grid place-items-center" data-testid="wealth-goal-ring">
      <svg width="220" height="220" className="-rotate-90">
        <circle cx="110" cy="110" r={R} fill="none" stroke="#24292F" strokeWidth="14" />
        <motion.circle cx="110" cy="110" r={R} fill="none" stroke="#E2674B" strokeWidth="14"
          strokeLinecap="round" strokeDasharray={C}
          initial={{ strokeDashoffset: C }} animate={{ strokeDashoffset: C * (1 - pct) }}
          transition={{ duration: 1, ease: "easeOut" }} />
      </svg>
      <div className="absolute text-center px-6">
        <div className="font-serif text-5xl text-brand leading-none">{years}</div>
        <div className="text-[11px] uppercase tracking-widest text-mutedText mt-2">years to<br />mortgage-free</div>
        <div className="text-[11px] text-emerald-300/80 mt-1">by {year}</div>
      </div>
    </div>
  );
}

const Slider = ({ label, value, min, max, step, onChange, fmt, testId }) => (
  <div>
    <div className="flex items-center justify-between text-sm">
      <span className="text-forest/80">{label}</span>
      <span className="font-medium text-brand">{fmt(value)}</span>
    </div>
    <input data-testid={testId} type="range" min={min} max={max} step={step} value={value}
      onChange={(e) => onChange(Number(e.target.value))}
      className="mt-2 w-full accent-brand" />
  </div>
);

function TrendBlock({ tone, title, startYear, startVal, endYear, endVal, dir }) {
  const c = tone === "red"
    ? { bg: "bg-red-500/10", bd: "border-red-500/25", t: "text-red-300", bar: "bg-red-400", lite: "text-red-200" }
    : { bg: "bg-emerald-500/10", bd: "border-emerald-500/25", t: "text-emerald-300", bar: "bg-emerald-400", lite: "text-emerald-200" };
  const Arrow = dir === "down" ? ArrowDown : ArrowUp;
  return (
    <div className={`rounded-lg ${c.bg} border ${c.bd} px-3 py-2`}>
      <div className={`flex items-center gap-2 text-[11px] uppercase tracking-widest ${c.t}/70`}>
        <span className={`w-3 h-[3px] rounded ${c.bar}`} /> {title}
      </div>
      <div className="mt-1.5 flex items-center justify-between text-sm">
        <span className={`${c.t}/80`}>{startYear}</span>
        <span className={`font-medium ${c.lite}`}>{money(startVal)}</span>
      </div>
      <div className={`flex items-center justify-center ${tone === "red" ? "text-red-400" : "text-emerald-400"}`}><Arrow size={13} /></div>
      <div className="flex items-center justify-between text-sm">
        <span className={`${c.t}/80`}>{endYear}</span>
        <span className={`font-semibold ${c.t}`}>{money(endVal)}</span>
      </div>
    </div>
  );
}

function PropertyCard({ p, i, endYear, testId }) {
  const Icon = i === 0 ? Home : Building2;
  return (
    <motion.div data-testid={testId}
      initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.12 }}
      className="flex-1 min-w-[200px] rounded-2xl border border-line bg-white/[0.03] p-4">
      <div className="flex items-center justify-between">
        <Icon size={18} className="text-brand" />
        <span className="text-[10px] uppercase tracking-widest text-brand">{p.tag}</span>
      </div>
      <div className="mt-3 font-medium text-forest text-sm">{p.label}</div>
      <div className="font-serif text-xl text-brand mt-0.5">{money(p.price)}</div>
      <div className="mt-3 space-y-2">
        <TrendBlock tone="red" title={p.redTitle} dir="down"
          startYear={START_YEAR} startVal={p.redStart} endYear={endYear} endVal={p.redEnd} />
        <TrendBlock tone="green" title={p.greenTitle} dir="up"
          startYear={START_YEAR} startVal={p.greenStart} endYear={endYear} endVal={p.greenEnd} />
      </div>
    </motion.div>
  );
}

export default function Wealth() {
  const { data, next, back } = useJourney();
  const sel = data.selected_home || {};
  const price = sel.purchase_price || (data.matches?.best_life?.base_price || 0) + (data.budget?.land_price || 0) || 700000;
  const deposit = data.finance_inputs?.deposit || data.budget?.deposit || 0;
  const principal = Math.max(price - deposit, 0);

  const [portfolio, setPortfolio] = useState(2);
  const [growth, setGrowth] = useState(5);   // investment capital growth p.a.
  const g = growth / 100;

  const homeMonthly = useMemo(() => monthlyRepay(principal, 30), [principal]);
  const amortBal = useMemo(() => (months) => {
    const r = RATE / 12; let bal = principal;
    for (let m = 0; m < months; m++) bal = Math.max(0, bal * (1 + r) - homeMonthly);
    return bal;
  }, [principal, homeMonthly]);

  // Capital growth of ONE investment held t years (value gain only — deposits & loans are repaid on sale).
  const growthPer = useMemo(() => (t) => INV_PRICE * ((1 + g) ** t - 1), [g]);

  // For N investments, first year the combined capital growth covers the DECLINING home loan.
  const optionFor = useMemo(() => (N) => {
    for (let t = 1; t <= HORIZON; t++) {
      const hb = amortBal(t * 12);
      const grown = N * growthPer(t);
      if (grown >= hb) return { t, year: START_YEAR + t, years: t, homeBal: Math.round(hb), grown: Math.round(grown) };
    }
    const t = HORIZON, hb = amortBal(t * 12), grown = N * growthPer(t);
    return { t, year: START_YEAR + t, years: t, homeBal: Math.round(hb), grown: Math.round(grown) };
  }, [amortBal, growthPer]);

  const options = useMemo(() => PORTFOLIOS.map((p) => ({ ...p, ...optionFor(p.n) })), [optionFor]);
  const active = options.find((o) => o.n === portfolio);
  const t = active.t, endYear = active.year;

  const homeLoanEnd = Math.round(amortBal(t * 12));
  const homeValEnd = price * (1 + HOME_G) ** t;

  const properties = useMemo(() => {
    const arr = [{
      label: "Your home", tag: "You live here", price,
      redTitle: "Loan balance", redStart: principal, redEnd: homeLoanEnd,
      greenTitle: "Home equity", greenStart: Math.round(price - principal), greenEnd: Math.round(homeValEnd - homeLoanEnd),
    }];
    const valEnd = Math.round(INV_PRICE * (1 + g) ** t);
    for (let k = 1; k <= portfolio; k++) {
      arr.push({
        label: `Investment ${k}`, tag: "Bought today", price: INV_PRICE,
        redTitle: "Loan (interest-only)", redStart: INV_LOAN, redEnd: INV_LOAN,
        greenTitle: "Capital growth", greenStart: 0, greenEnd: Math.round(valEnd - INV_PRICE),
      });
    }
    return arr;
  }, [portfolio, price, principal, homeLoanEnd, homeValEnd, t, g]);

  const perGrowth = Math.round(growthPer(t));

  return (
    <StageFrame testId="stage-wealth" className="px-6 md:px-14 pt-16 pb-28">
      <div className="max-w-6xl mx-auto">
        <Eyebrow>Your Home, Your Wealth</Eyebrow>
        <h1 className="font-serif text-3xl md:text-5xl font-light tracking-tight text-forest mt-3 max-w-3xl">
          Let your investments' growth pay off your home loan — years sooner.
        </h1>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 mt-10 items-center">
          <div className="flex justify-center"><Ring years={active.years} year={endYear} /></div>

          <div className="space-y-6">
            <div>
              <span className="label-eyebrow text-mutedText">1 · How many investment properties?</span>
              <div className="mt-2 grid grid-cols-3 gap-2" data-testid="wealth-portfolio-options">
                {options.map((o) => (
                  <button key={o.n} data-testid={`wealth-portfolio-${o.n}`} onClick={() => setPortfolio(o.n)}
                    className={`rounded-xl border px-3 py-3 text-left transition ${portfolio === o.n ? "border-brand bg-brand/10" : "border-line hover:border-brand/50"}`}>
                    <div className={`text-sm font-semibold ${portfolio === o.n ? "text-brand" : "text-forest"}`}>{o.n} {o.n === 1 ? "property" : "properties"}</div>
                    <div className="text-[11px] uppercase tracking-widest text-mutedText mt-0.5">{o.label}</div>
                    <div className="mt-2 text-xs text-forest/70">Home paid off in</div>
                    <div className="font-serif text-lg text-emerald-300 leading-tight">{o.years} yrs</div>
                    <div className="text-[11px] text-mutedText">by {o.year}</div>
                  </button>
                ))}
              </div>
            </div>

            <Slider testId="wealth-growth-slider" label="Investment growth per year" value={growth} min={3} max={7} step={1}
              onChange={setGrowth} fmt={(v) => `${v}%`} />

            <div className="rounded-2xl border border-line bg-white/5 p-4 text-sm text-forest/80" data-testid="wealth-summary">
              With <span className="text-brand font-semibold">{portfolio} investment {portfolio === 1 ? "property" : "properties"}</span> growing
              at {growth}%, their combined capital growth clears your <span className="text-red-300 font-semibold">{money(principal)}</span> home
              loan by <span className="text-emerald-300 font-semibold">{endYear}</span> — {active.years} years from now.
            </div>
          </div>
        </div>

        <div className="mt-12" data-testid="wealth-cascade">
          <span className="label-eyebrow text-mutedText">How the numbers work — at {endYear}</span>
          <p className="text-sm text-mutedText mt-1 max-w-3xl">
            Each {money(INV_PRICE)} investment is held on an interest-only loan, so its loan stays flat while the
            property grows. Only the <span className="text-emerald-300">capital growth</span> ({money(perGrowth)} each by {endYear})
            is counted — enough combined to wipe out your remaining home loan (red), which is also shrinking as you repay it.
          </p>

          <div className="mt-6 flex flex-wrap items-stretch gap-3">
            {properties.map((p, i) => (
              <React.Fragment key={i}>
                <PropertyCard p={p} i={i} endYear={endYear} testId={`wealth-property-${i}`} />
                {i < properties.length - 1 && (
                  <div className="self-center text-brand"><ArrowRight size={20} /></div>
                )}
              </React.Fragment>
            ))}
          </div>

          <motion.div key={portfolio} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.5 }}
            className="mt-5 rounded-2xl border border-emerald-500/40 bg-emerald-500/10 p-5 flex flex-wrap items-center gap-4"
            data-testid="wealth-crossover-banner">
            <CheckCircle2 className="text-emerald-400" size={22} />
            <div className="text-forest/90 text-sm">
              By {endYear}, your {portfolio} investment {portfolio === 1 ? "property has" : "properties have"} grown{" "}
              <span className="text-emerald-300 font-semibold">{money(active.grown)}</span> in value — more than the{" "}
              <span className="text-red-300 font-semibold">{money(active.homeBal)}</span> still owing on your home.
              Realise that growth and your home is <span className="font-semibold text-forest">paid off outright.</span>
            </div>
          </motion.div>

          <div className="mt-4 rounded-2xl border border-brand/40 bg-brand/10 p-4" data-testid="wealth-cgt-note">
            <p className="text-xs text-forest/90 leading-relaxed">
              <span className="font-semibold text-brand">Note:</span> these figures do not take into account taxes that
              may apply to the capital gain. You must seek advice from your accountant before using this method to pay
              down your mortgage.
            </p>
          </div>

          <p className="mt-3 text-[11px] text-mutedText/70 max-w-3xl">
            Indicative only. Assumes interest-only investment loans with deposits funded separately; growth compounded
            annually and home loan amortised at {(RATE * 100).toFixed(1)}% over 30 years. Excludes buying/selling costs, tax and rent.
          </p>
        </div>
      </div>

      <ConsultantNav testId="stage-wealth" onBack={back} onNext={next} nextLabel="Lock in your finance" />
    </StageFrame>
  );
}
